import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { PushState, StaffAlertRow } from '@bbbk/schema';
import { Icon } from '../common';
import { api, ApiError } from '../../lib/api';
import {
  alertsStateFor,
  cardRead,
  currentSubscription,
  isStandalone,
  latestOnly,
  needsHomeScreen,
  onVisible,
  permissionNow,
  pushSupported, readAfter,
  subscribePush,
  subscriptionIsCurrent,
  subscriptionKey,
  unsubscribePush,
  type AlertsState,
  type ServerRead,
} from './pushClient';
import { turnOnSequence, turnOffSequence, testSequence, type AlertActionDeps } from './alertActions';

// The Info tab (shift-alerts-info-tab, flow 1.1). One card with four states
// and the one action a helper ever takes — Turn on — plus the install walk,
// worded for any phone (an iPhone is the one that needs it; Android pushes
// from the browser), then two quiet rows: who is signed in, and what AM and PM
// mean. Patricia's preview shows the same card with the helper's server state
// and no buttons (AC11): the preview never subscribes the admin's browser.


const sinceLabel = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', timeZone: 'Europe/Dublin' });

const ONE_LINE = 'One alert to this phone when Patricia changes your shifts. Nothing else.';

export type { ServerRead };

export function AlertsCard({
  state,
  since,
  busy,
  preview,
  error,
  read = 'ready',
  heldHere = false,
  onTurnOn,
  onTurnOff,
  onTest,
  onCheckAgain,
}: {
  state: AlertsState;
  since: string | null;
  busy: boolean;
  preview?: boolean;
  error?: string | null;
  /** The server's answer: until it lands the card says Checking…, so an On phone never flashes Ready and a helper is never called Off before the read (review, PR #278, round 5). */
  read?: ServerRead;
  /** The server holds THIS phone — so a Blocked card can still Turn off (review, PR #278, round 8). */
  heldHere?: boolean;
  onTurnOn: () => void;
  onTurnOff: () => void;
  onTest: () => void;
  onCheckAgain: () => void;
}) {
  const tone = state === 'on' ? 'on' : state === 'blocked' ? 'blocked' : 'off';
  const head = (
    <div className="alerts-head">
      <span className="alerts-ic">
        <Icon name="bell" size={18} />
      </span>
      <div>
        <div className="alerts-title">Shift alerts</div>
        <div className="alerts-sub">{ONE_LINE}</div>
      </div>
    </div>
  );
  const pill = (kind: 'live' | 'attention', text: string) => <span className={'alerts-pill ' + kind}>{text}</span>;

  // Until the server has answered there is no state to show and nothing safe
  // to press — live or preview.
  if (read === 'pending') {
    return (
      <section className="alerts-card off">
        {head}
        <div className="alerts-state">
          <span>Checking…</span>
        </div>
      </section>
    );
  }

  // The server said alerts are not configured (503 `unavailable`): the
  // kennel's setup is unfinished, this phone is fine, and nothing is offered
  // but the retry — live and preview alike (Freddi, 2026-10-23).
  if (read === 'unavailable') {
    return (
      <section className="alerts-card off">
        {head}
        <div className="alerts-state">
          <span>Shift alerts are not switched on for Beeches Brook yet.</span>
        </div>
        <p className="alerts-why">Nothing to do on your phone. Once the kennel's setup is finished, this card will let you switch them on.</p>
        {!preview && (
          <button type="button" className="alerts-btn sec" disabled={busy} onClick={onCheckAgain}>Check again</button>
        )}
      </section>
    );
  }

  // A live read that failed: nothing is known about this phone, so nothing
  // is offered but the retry — never Ready with a Turn on (review, PR #278,
  // round 6).
  if (read === 'failed' && !preview) {
    return (
      <section className="alerts-card off">
        {head}
        <div className="alerts-state">
          <span>Couldn't check this phone. Try again.</span>
        </div>
        <button type="button" className="alerts-btn sec" disabled={busy} onClick={onCheckAgain}>Check again</button>
      </section>
    );
  }

  // The preview: the server's truth for this helper, in the card's own words,
  // and nothing to press. A read that failed says so, never Off.
  if (preview) {
    return (
      <section className={'alerts-card ' + tone}>
        {head}
        <div className="alerts-state">
          {read === 'failed' ? (
            <span>Couldn't check this phone. Close the preview and try again.</span>
          ) : (
            <>
              {state === 'on' ? pill('live', 'On') : pill('attention', 'Off')}
              <span>{state === 'on' && since ? `This phone · since ${sinceLabel(since)}` : 'Not on this phone yet'}</span>
            </>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className={'alerts-card ' + tone}>
      {head}
      {state === 'install' && (
        <>
          <div className="alerts-state">
            {pill('attention', 'Off')}
            <span>First, add Beeches Brook to your Home Screen</span>
          </div>
          <p className="alerts-why">Alerts only reach an app on your Home Screen. Three taps, once.</p>
          <ol className="alerts-howto">
            <li>
              Tap your browser's{' '}
              <span className="alerts-k">
                <Icon name="upload" size={14} />
                Share
              </span>{' '}
              or{' '}
              <span className="alerts-k">
                <Icon name="menu" size={14} />
                menu
              </span>{' '}
              button
            </li>
            <li>
              Choose{' '}
              <span className="alerts-k">
                <Icon name="plus" size={14} />
                Add to Home Screen
              </span>{' '}
              (or <b>Install app</b>), then <b>Add</b>
            </li>
            <li>
              Open <b>Beeches Brook</b> from your Home Screen and come back to Info
            </li>
          </ol>
          <button type="button" className="alerts-btn pri dim" disabled>Turn on</button>
          <p className="alerts-hint">Turn on works once you open from the Home Screen.</p>
        </>
      )}
      {state === 'ready' && (
        <>
          <div className="alerts-state">
            {pill('attention', 'Off')}
            <span>Ready. Your phone will ask once.</span>
          </div>
          {/* How to allow alerts, worded for any phone, and the fact that Turn on
              is itself the test: the server sends one alert the moment it has
              this phone (Freddi, 2026-10-23). "Should", because that send is
              best effort — a push-service 500 still stores the phone and
              answers 200 (review, PR #280); Send a test is the retry. */}
          <ol className="alerts-howto">
            <li>
              Tap <b>Turn on</b>
            </li>
            <li>
              When your phone asks, choose <b>Allow</b>
            </li>
            <li>
              A test alert should land on this phone within a few seconds. If nothing arrives, tap <b>Send a test</b> once the card reads On.
            </li>
          </ol>
          <button type="button" className="alerts-btn pri" disabled={busy} onClick={onTurnOn}>Turn on</button>
          {error && <p className="alerts-hint error">{error}</p>}
          <p className="alerts-hint">
            If your phone never asks, or you chose Don't allow: open its <b>Settings</b>, find <b>Beeches Brook</b> under <b>Notifications</b> (or <b>Apps</b>), allow them, and come back here.
          </p>
        </>
      )}
      {state === 'on' && (
        <>
          <div className="alerts-state">
            {pill('live', 'On')}
            <span>This phone{since ? ` · since ${sinceLabel(since)}` : ''}</span>
          </div>
          <div className="alerts-acts">
            <button type="button" className="alerts-btn sec" disabled={busy} onClick={onTest}>Send a test</button>
            <button type="button" className="alerts-link" disabled={busy} onClick={onTurnOff}>Turn off</button>
          </div>
          {error && <p className="alerts-hint error">{error}</p>}
        </>
      )}
      {state === 'blocked' && (
        <>
          <div className="alerts-state">
            {pill('attention', 'Blocked')}
            <span>Your phone is blocking alerts</span>
          </div>
          <p className="alerts-why">
            To allow them: open your phone's <b>Settings</b>, find <b>Beeches Brook</b> under <b>Notifications</b> (or <b>Apps</b>), and turn notifications on. Then come back here.
          </p>
          <div className="alerts-acts">
            <button type="button" className="alerts-btn sec" disabled={busy} onClick={onCheckAgain}>Check again</button>
            {heldHere && <button type="button" className="alerts-link" disabled={busy} onClick={onTurnOff}>Turn off</button>}
          </div>
        </>
      )}
      {state === 'unsupported' && (
        <div className="alerts-state">
          {pill('attention', 'Off')}
          <span>This browser can't receive alerts. Open Beeches Brook on your phone.</span>
        </div>
      )}
    </section>
  );
}

export function InfoPanelBody({ email, card }: { email: string | null; card: ReactNode }) {
  return (
    <div className="staff-panel">
      {card}
      <section className="staff-info-card">
        <div className="staff-info-row">
          <span>Signed in as</span>
          <b>{email ?? '—'}</b>
        </div>
      </section>
    </div>
  );
}

export interface ShiftAlerts {
  state: AlertsState;
  read: ServerRead;
  /** The server holds this phone's own subscription (so Blocked can still Turn off). */
  heldHere: boolean;
  since: string | null;
  busy: boolean;
  error: string | null;
  turnOn: () => void;
  turnOff: () => void;
  test: () => void;
  checkAgain: () => void;
}

// The live card's brain: the server's row (GET /api/me/push) plus the
// browser's facts, re-read after every action. `preview` reads the helper's
// state from Patricia's card endpoint instead, and offers no actions.
export function useShiftAlerts(preview?: { staffId: string }): ShiftAlerts {
  const [server, setServer] = useState<{ on: boolean; since: string | null; endpoint: string | null; publicKey: string | null }>({ on: false, since: null, endpoint: null, publicKey: null });
  const [facts, setFacts] = useState<{ permission: NotificationPermission; endpoint: string | null; key: string | null }>({ permission: 'default', endpoint: null, key: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverRead, setServerRead] = useState<ServerRead>('pending');
  const [browserDone, setBrowserDone] = useState(false);

  // Only the latest read may land: a mount's or an action's slow answer must
  // not arrive after the visibility refresh and put the facts back (round 7).
  const fetchServer = useMemo(
    () =>
      latestOnly(async () => {
        if (preview) {
          const r = await api<StaffAlertRow[]>('/staff/alerts');
          const row = r.data.find((x) => x.id === preview.staffId);
          return { on: row?.state === 'on', since: row?.since ?? null, endpoint: null, publicKey: null };
        }
        const r = await api<PushState>('/me/push');
        return { on: r.data.on, since: r.data.since, endpoint: r.data.endpoint, publicKey: r.data.publicKey };
      }),
    [preview?.staffId],
  );
  const readServer = useCallback(async () => {
    try {
      const next = await fetchServer();
      if (next === undefined) return; // superseded by a newer read
      setServer(next);
      setServerRead('ready');
    } catch (e) {
      const after = readAfter(e);
      setServerRead((was) => (was === 'ready' && after === 'failed' ? 'ready' : after));
      throw e;
    }
  }, [fetchServer]);

  const readBrowser = useCallback(async () => {
    try {
      if (preview || !pushSupported()) return;
      const sub = await currentSubscription();
      setFacts({ permission: permissionNow(), endpoint: sub?.endpoint ?? null, key: subscriptionKey(sub) });
    } finally {
      setBrowserDone(true);
    }
  }, [preview?.staffId]);

  useEffect(() => {
    readServer().catch((e) => {
      if (!(e instanceof ApiError && (e.status === 401 || readAfter(e) === 'unavailable'))) setError("Couldn't check this phone. Try again later.");
    });
    readBrowser();
  }, [readServer, readBrowser]);

  // And again whenever the app comes back to the front: a phone replaced
  // while this one was in the background must not keep reading On from the
  // facts it mounted with (review, PR #278, round 5).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    return onVisible(document, () => {
      readServer().catch(() => undefined);
      readBrowser();
    });
  }, [readServer, readBrowser]);

  const run = (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    work()
      .catch((e) => setError(e instanceof Error && e.message ? e.message : "That didn't work. Try again."))
      .finally(() => {
        setBusy(false);
        readBrowser();
      });
  };

  // The three multi-step actions are pinned sequences (alertActions.ts): Turn
  // on releases a subscription the server does not know before it subscribes,
  // the server row goes before the browser subscription on Turn off, and Send
  // a test re-reads the server however it ends (review, PR #278).
  const actionDeps: AlertActionDeps = {
    localEndpoint: async () => (await currentSubscription())?.endpoint ?? null,
    localKey: async () => subscriptionKey(await currentSubscription()),
    api: (path, opts) => api(path, opts),
    subscribe: subscribePush,
    unsubscribe: unsubscribePush,
    readServer,
    userAgent: () => navigator.userAgent,
  };
  const turnOn = () => run(() => turnOnSequence(actionDeps, { publicKey: server.publicKey, serverEndpoint: server.endpoint }));
  const turnOff = () => run(() => turnOffSequence(actionDeps));
  const test = () => run(() => testSequence(actionDeps));

  const checkAgain = () => run(async () => readServer());

  const state: AlertsState = preview
    ? server.on
      ? 'on'
      : 'ready'
    : alertsStateFor({
        supported: pushSupported(),
        permission: facts.permission,
        needsInstall: typeof navigator !== 'undefined' && needsHomeScreen(navigator.userAgent, isStandalone()),
        // On means THIS phone under the CURRENT key (review, PR #278, rounds 2 and 6).
        subscribed: subscriptionIsCurrent(facts, { endpoint: server.endpoint, publicKey: server.publicKey }),
      });

  const read = cardRead(serverRead, browserDone, !!preview);
  const heldHere = !preview && server.on && subscriptionIsCurrent(facts, { endpoint: server.endpoint, publicKey: server.publicKey });
  return { state, read, heldHere, since: server.since, busy, error, turnOn, turnOff, test, checkAgain };
}

export function InfoPanel({ email, alerts, preview }: { email: string | null; alerts: ShiftAlerts; preview?: boolean }) {
  return (
    <InfoPanelBody
      email={email}
      card={
        <AlertsCard
          state={alerts.state}
          read={alerts.read}
          heldHere={alerts.heldHere}
          since={alerts.since}
          busy={alerts.busy}
          preview={preview}
          error={alerts.error}
          onTurnOn={alerts.turnOn}
          onTurnOff={alerts.turnOff}
          onTest={alerts.test}
          onCheckAgain={alerts.checkAgain}
        />
      }
    />
  );
}
